import { supabase } from '@/integrations/supabase/client';

/**
 * Upload a blob to Supabase storage and return the public URL
 */
export async function uploadMediaToStorage(
  blob: Blob,
  userId: string,
  listingId: string,
  type: 'collage' | 'video' | 'pdf'
): Promise<string | null> {
  try {
    // Guard against empty blobs (e.g. aborted video generation). Uploading a
    // 0-byte file results in storage objects that return HTTP 416 on playback.
    if (!blob || blob.size === 0) {
      console.warn(`[uploadMediaToStorage] Skipping empty ${type} blob`);
      return null;
    }
    // Determine extension and mime type based on type or actual blob type
    let extension: string;
    let mimeType: string;
    
    if (type === 'video') {
      // Cinematic videos are MP4 (ffmpeg.wasm concat); slideshows are WebM.
      const isMp4 = blob.type.includes('mp4');
      extension = isMp4 ? 'mp4' : 'webm';
      mimeType = isMp4 ? 'video/mp4' : 'video/webm';
    } else if (type === 'pdf' || blob.type === 'application/pdf') {
      extension = 'pdf';
      mimeType = 'application/pdf';
    } else {
      extension = 'png';
      mimeType = 'image/png';
    }
    const filePath = `${userId}/${listingId}/${type}.${extension}`;

    // Upload to storage bucket
    const { data, error } = await supabase.storage
      .from('listing-media')
      .upload(filePath, blob, {
        contentType: mimeType,
        upsert: true,
      });

    if (error) {
      console.error(`Failed to upload ${type}:`, error);
      return null;
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('listing-media')
      .getPublicUrl(filePath);

    return urlData?.publicUrl || null;
  } catch (err) {
    console.error(`Error uploading ${type}:`, err);
    return null;
  }
}

/**
 * Upload a photo (original or enhanced) to Supabase storage and return the public URL
 */
export async function uploadPhotoToStorage(
  photoUrl: string,
  userId: string,
  listingId: string,
  photoIndex: number,
  type: 'original' | 'enhanced'
): Promise<string | null> {
  try {
    // Skip if already a storage URL
    if (photoUrl.includes('supabase.co/storage') || photoUrl.includes('supabase.in/storage')) {
      console.log(`✓ Photo ${photoIndex} (${type}) already in storage`);
      return photoUrl;
    }

    console.log(`📤 Uploading ${type} photo ${photoIndex}...`, photoUrl.substring(0, 50));

    // Convert URL to blob
    const blob = await urlToBlob(photoUrl);
    if (!blob) {
      console.error(`❌ Failed to convert ${type} photo ${photoIndex} to blob - URL type: ${photoUrl.substring(0, 10)}`);
      return null;
    }

    console.log(`✓ Converted to blob: ${blob.size} bytes, type: ${blob.type}`);

    // Determine extension
    const extension = blob.type.includes('png') ? 'png' : 'jpg';
    const filePath = `${userId}/${listingId}/photo_${photoIndex}_${type}.${extension}`;

    // Upload to storage bucket
    const { error } = await supabase.storage
      .from('listing-media')
      .upload(filePath, blob, {
        contentType: blob.type || 'image/jpeg',
        upsert: true,
      });

    if (error) {
      console.error(`❌ Failed to upload ${type} photo ${photoIndex}:`, error);
      return null;
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('listing-media')
      .getPublicUrl(filePath);

    console.log(`✅ Uploaded ${type} photo ${photoIndex}: ${urlData?.publicUrl?.substring(0, 60)}...`);
    return urlData?.publicUrl || null;
  } catch (err) {
    console.error(`❌ Error uploading ${type} photo ${photoIndex}:`, err);
    return null;
  }
}

/**
 * Upload only original photos to storage in parallel
 * This can run while enhancement is happening to save time
 */
export async function uploadOriginalPhotosToStorage(
  photos: Array<{ preview: string }>,
  userId: string,
  listingId: string,
  onProgress?: (current: number, total: number) => void
): Promise<string[]> {
  const total = photos.length;
  let completed = 0;
  
  // Upload all originals in parallel (up to 3 at a time)
  const uploadPromises = photos.map(async (photo, i) => {
    const originalUrl = await uploadPhotoToStorage(
      photo.preview,
      userId,
      listingId,
      i,
      'original'
    );
    
    completed++;
    onProgress?.(completed, total);
    
    // CRITICAL: Never fall back to blob URLs
    if (originalUrl) {
      return originalUrl;
    } else if (!photo.preview.startsWith('blob:')) {
      return photo.preview;
    }
    // Blob that couldn't upload - this will break, log it
    console.error(`❌ Failed to upload original photo ${i} - blob URL will not persist`);
    return photo.preview;
  });
  
  return Promise.all(uploadPromises);
}

/**
 * Upload enhanced photos to storage (after enhancement is complete)
 */
export async function uploadEnhancedPhotosToStorage(
  enhancedUrls: string[],
  originalStorageUrls: string[],
  userId: string,
  listingId: string,
  onProgress?: (current: number, total: number) => void
): Promise<string[]> {
  const total = enhancedUrls.length;
  let completed = 0;
  
  // Upload all enhanced in parallel
  const uploadPromises = enhancedUrls.map(async (enhancedUrl, i) => {
    // Skip if same as original (no enhancement happened)
    if (enhancedUrl === originalStorageUrls[i]) {
      completed++;
      onProgress?.(completed, total);
      return originalStorageUrls[i];
    }
    
    const storageUrl = await uploadPhotoToStorage(
      enhancedUrl,
      userId,
      listingId,
      i,
      'enhanced'
    );
    
    completed++;
    onProgress?.(completed, total);
    
    return storageUrl || enhancedUrl;
  });
  
  return Promise.all(uploadPromises);
}

/**
 * Upload all photos to storage (both original and enhanced)
 * Returns an object with arrays of permanent URLs
 */
export async function uploadAllPhotosToStorage(
  photos: Array<{ preview: string; enhanced?: string }>,
  userId: string,
  listingId: string,
  onProgress?: (current: number, total: number) => void
): Promise<{ originalUrls: string[]; enhancedUrls: string[] }> {
  const total = photos.length * 2;
  let current = 0;
  
  // Upload originals first (in parallel)
  const originalUrls = await uploadOriginalPhotosToStorage(
    photos,
    userId,
    listingId,
    (c, t) => {
      current = c;
      onProgress?.(current, total);
    }
  );
  
  // Then upload enhanced (in parallel)
  const enhancedSources = photos.map((p, i) => p.enhanced || originalUrls[i]);
  const enhancedUrls = await uploadEnhancedPhotosToStorage(
    enhancedSources,
    originalUrls,
    userId,
    listingId,
    (c, t) => {
      current = photos.length + c;
      onProgress?.(current, total);
    }
  );

  return { originalUrls, enhancedUrls };
}

/**
 * Upload CLEAN enhanced photos to the PRIVATE `listing-clean` bucket and return
 * their storage paths (not public URLs — the bucket is write-only for the user).
 *
 * Used for trial listings: the clean originals are withheld here while only a
 * watermarked copy is served publicly. `apply-trial-watermark` (service role)
 * reads these paths to produce the watermarked versions, and payment promotes
 * them to public. Throws if any upload fails so the caller never publishes a
 * partially-protected listing.
 */
export async function uploadCleanEnhancedToPrivate(
  enhancedUrls: string[],
  userId: string,
  listingId: string,
): Promise<string[]> {
  const paths = await Promise.all(
    enhancedUrls.map(async (url, i) => {
      const blob = await urlToBlob(url);
      if (!blob) throw new Error(`Could not read enhanced photo ${i} for clean upload`);
      const ext = blob.type.includes('png') ? 'png' : 'jpg';
      const path = `${userId}/${listingId}/photo_${i}.${ext}`;
      const { error } = await supabase.storage
        .from('listing-clean')
        .upload(path, blob, { contentType: blob.type || 'image/jpeg', upsert: true });
      if (error) throw new Error(`Failed to upload clean photo ${i}: ${error.message}`);
      return path;
    }),
  );
  return paths;
}

/**
 * Check if a URL is a valid, fetchable URL (not a blob URL that's expired)
 */
export function isValidPhotoUrl(url: string): boolean {
  if (!url) return false;
  
  // Blob URLs are only valid in the same browser session
  if (url.startsWith('blob:')) return false;
  
  // Data URLs are always valid (self-contained)
  if (url.startsWith('data:')) return true;
  
  // HTTP/HTTPS URLs are assumed valid
  if (url.startsWith('http://') || url.startsWith('https://')) return true;
  
  return false;
}

/**
 * Convert a data URL or blob URL to a Blob
 */
export async function urlToBlob(url: string): Promise<Blob | null> {
  try {
    if (url.startsWith('data:')) {
      // Data URL - convert to blob
      const response = await fetch(url);
      return await response.blob();
    } else if (url.startsWith('blob:')) {
      // Blob URL - fetch and ensure proper MIME type
      const response = await fetch(url);
      const blob = await response.blob();
      
      // If blob has no proper MIME type, detect it from content or default to JPEG
      if (!blob.type || blob.type === 'application/octet-stream') {
        // Read first bytes to detect image type
        const buffer = await blob.slice(0, 12).arrayBuffer();
        const bytes = new Uint8Array(buffer);
        
        let detectedType = 'image/jpeg'; // Default fallback
        
        // Check magic bytes for common image formats
        if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
          detectedType = 'image/jpeg';
        } else if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
          detectedType = 'image/png';
        } else if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
          detectedType = 'image/gif';
        } else if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
                   bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
          detectedType = 'image/webp';
        }
        
        console.log(`🔍 Detected MIME type from magic bytes: ${detectedType}`);
        
        // Create new blob with correct MIME type
        return new Blob([blob], { type: detectedType });
      }
      
      return blob;
    } else if (url.startsWith('http://') || url.startsWith('https://')) {
      // HTTP URL - fetch it
      const response = await fetch(url);
      if (response.ok) {
        return await response.blob();
      }
    }
    return null;
  } catch (err) {
    console.error('Error converting URL to blob:', err);
    return null;
  }
}
